import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, KeyRound, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import CreateApiKeyModal from "./CreateApiKeyModal";

const toData = response =>
  response?.data?.data ?? response?.data?.payload ?? response?.data ?? null;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

export default function ApiKeysSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [freshSecret, setFreshSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/apikeys", {
        __silentGenericToast: true,
      });
      const data = toData(res);
      const list = Array.isArray(data) ? data : data?.items || data?.keys || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to load API keys"), {
          toastId: "integrations-apikeys-load",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async payload => {
    setBusy(true);
    try {
      const res = await axiosClient.post("/apikeys", payload, {
        __silentGenericToast: true,
      });
      const data = toData(res) || {};
      const key = data?.rawKey || data?.secret || data?.apiKey || null;
      setFreshSecret(
        key
          ? {
              rawKey: key,
              keyPrefix: data?.keyPrefix || "",
            }
          : null
      );
      setShowCreate(false);
      toast.success("API key created");
      await load();
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to create API key"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async row => {
    if (!row?.id) return;
    if (!window.confirm(`Revoke API key "${row.name || row.keyPrefix}"?`)) return;

    try {
      await axiosClient.post(`/apikeys/${row.id}/revoke`, null, {
        __silentGenericToast: true,
      });
      toast.success("API key revoked");
      await load();
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to revoke API key"));
      }
    }
  };

  const hasRows = useMemo(() => rows.length > 0, [rows]);

  const copySecret = async value => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">API Keys</h2>
          <p className="mt-1 text-sm text-slate-500">
            Use these keys for server-to-server Public API calls. Raw keys are
            shown once, and revoking a key takes effect immediately.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm text-white"
        >
          <KeyRound size={16} />
          Create API Key
        </button>
      </div>

      {freshSecret?.rawKey ? (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-amber-800 font-medium mb-2">
            This secret is shown once. Store it securely now.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded border border-amber-300 bg-white px-2 py-1 text-xs text-slate-700">
              {freshSecret.rawKey}
            </code>
            <button
              type="button"
              onClick={() => copySecret(freshSecret.rawKey)}
              className="inline-flex items-center gap-1 rounded border border-amber-400 px-2 py-1 text-xs text-amber-800"
            >
              <Copy size={14} /> Copy
            </button>
            <button
              type="button"
              onClick={() => setFreshSecret(null)}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : !hasRows ? (
        <p className="text-sm text-slate-500">No API keys found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Prefix</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Last Used</th>
                <th className="py-2 pr-3">Revoked</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3">{row.name || "-"}</td>
                  <td className="py-2 pr-3">{row.keyPrefix || "-"}</td>
                  <td className="py-2 pr-3">{formatDate(row.createdAtUtc)}</td>
                  <td className="py-2 pr-3">{formatDate(row.lastUsedAtUtc)}</td>
                  <td className="py-2 pr-3">{formatDate(row.revokedAtUtc)}</td>
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => handleRevoke(row)}
                      disabled={!!row.revokedAtUtc}
                      className="inline-flex items-center gap-1 rounded border border-rose-300 px-2 py-1 text-rose-700 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateApiKeyModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        busy={busy}
      />
    </section>
  );
}
